// This is for client-side data fetching (CSR)
export const fetchStrapiData = async params => {
  const response = await fetch('/api/strapi', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    throw new Error(`Strapi API error: ${response.status} ${response.statusText}`);
  }

  const result = await response.json();
  return result;
};

// Define component structure for each page type
export const getComponentStructure = pageType => {
  const structures = {
    activities: [
      'hero',
      'listing',
      'category',
      'regionDiscipline',
      'article',
      'cta',
      'topLocation',
    ],
    destinations: [
      'hero',
      'listing',
      'category',
      'regionDiscipline',
      'article',
      'cta',
      'topLocation',
    ],
    country: [
      'hero',
      'description',
      'listing',
      'description',
      'category',
      'description',
      'regionDiscipline',
      'article',
      'faq',
      'cta',
      'topLocation',
    ],
    region: [
      'hero',
      'description',
      'listing',
      'description',
      'regionDiscipline',
      'description',
      'article',
      'faq',
      'cta',
      'topLocation',
    ],
    "region-discipline": [
      'hero',
      'listing',
      'description',
      'regionDiscipline',
      'description',
      'description',
      'article',
      'faq',
      'cta',
      'topLocation',
    ],
    discipline: [
      'hero',
      'listing',
      'description',
      'description',
      'regionDiscipline',
      'description',
      'description',
      'description',
      'article',
      'faq',
      'cta',
      'topLocation',
    ],
    stories: ['hero', 'category', 'article', 'regionDiscipline', 'cta', 'topLocation'],
    'article-category': ['hero', 'article', 'category', 'regionDiscipline', 'cta', 'topLocation'],
    article: ['hero', 'description', 'article', 'category', 'cta', 'topLocation'],
  };

  return structures[pageType] || structures.activities;
};

// Get required pageTypes for API call based on current page type
export const getRequiredPageTypes = (currentPageType) => {
  const pageTypes = ['article', 'region-discipline']; // Always fetch these base types

  switch (currentPageType) {
    case 'stories':
    case 'article-category':
    case 'article':
      pageTypes.push('article-category');
      break;

    case 'activities':
      pageTypes.push('discipline');
      break;

    case 'destinations':
      pageTypes.push('country');
      break;

    case 'country':
      pageTypes.push('region');
      break;

    default:
      pageTypes.push('discipline');
      break;
  }

  return pageTypes.join(',');
};

// Get filters for listing based on page type and tags
export const getlistingFilters = (pageType, tags) => {
  const countryTag = tags.find(t => t.name === 'country');
  const regionTag = tags.find(t => t.name === 'region');
  const disciplineTag = tags.find(t => t.name === 'discipline');

  switch (pageType) {
    case 'activities':
    case 'destinations':
      // No filter, sort by publish date
      return { sortBy: 'publishedAt', sortOrder: 'desc' };

    case 'country':
      return countryTag ? { country: countryTag.value } : {};

    case 'region':
      return regionTag ? { region: regionTag.value } : {};

    case 'discipline':
      return disciplineTag ? { discipline: disciplineTag.value } : {};

    case 'region-discipline':
      const filters = { sortBy: 'publishedAt', sortOrder: 'desc' };
      if (regionTag) filters.region = regionTag.value;
      if (disciplineTag) filters.discipline = disciplineTag.value;
      return filters;

    default:
      return { sortBy: 'publishedAt', sortOrder: 'desc' };
  }
};

// Get filters for article section based on current page tags
export const getArticleFilters = (currentPageTags, currentPageLocale = 'en') => {
  const filters = {
    pageType: 'article', // Always filter for article pageType
    locale: currentPageLocale, // Filter by locale
    tags: { $and: [] }, // Use AND condition for multiple tags
  };

  // Add all tags from current page to the AND filter
  currentPageTags.forEach(tag => {
    if (tag.name && tag.value) {
      filters.tags.$and.push({
        $elemMatch: {
          name: tag.name,
          value: tag.value,
        },
      });
    }
  });

  // If no tags, remove the tags filter
  if (filters.tags.$and.length === 0) {
    delete filters.tags;
  }

  return filters;
};

// Updated function to get filters for different sections
export const getSectionFilters = (sectionType, currentPageTags, currentPageLocale = 'en') => {
  switch (sectionType) {
    case 'article':
      return getArticleFilters(currentPageTags, currentPageLocale);

    case 'category':
      return {
        pageType: 'article-category',
        locale: currentPageLocale,
      };

    case 'regionDiscipline':
      // For regionDiscipline, we might want different logic
      return {
        pageType: 'region', // or 'discipline' based on requirements
        locale: currentPageLocale,
        tags: {
          $or: currentPageTags.map(tag => ({
            $elemMatch: {
              name: tag.name,
              value: tag.value,
            },
          })),
        },
      };

    default:
      return {
        locale: currentPageLocale,
      };
  }
};

// Get filters for category based on page type and tags
export const getcategoryFilters = (pageType, tags) => {
  const countryTag = tags.find(t => t.name === 'country');

  switch (pageType) {
    case 'stories':
    case 'article-category':
    case 'article':
      // Show all article-categories
      return { pageType: 'article-category' };

    case 'activities':
      // Show all disciplines
      return { pageType: 'discipline' };

    case 'destinations':
      // Show all countries
      return { pageType: 'country' };

    case 'country':
      // Show regions in country
      return countryTag
        ? {
            pageType: 'region',
            tags: { $elemMatch: { name: 'country', value: countryTag.value } },
          }
        : { pageType: 'region' };

    default:
      return { pageType: 'discipline' };
  }
};

// Filter related pages based on requirements - UPDATED TO USE NEW LOGIC
export const filterRelatedPages = (
  allPages,
  currentPageType,
  currentTags,
  currentLocale = 'en'
) => {

  if (!allPages || allPages.length === 0) {
    return {
      listing: [],
      regionDiscipline: [],
      article: [],
      category: [],
    };
  }

  // Filter for Listings Carousel
  const listingData = allPages.filter(page => {
    const filters = getlistingFilters(currentPageType, currentTags);

    if (filters.country) {
      return page.tag?.some(tag => tag.name === 'country' && tag.value === filters.country);
    }
    if (filters.region) {
      return page.tag?.some(tag => tag.name === 'region' && tag.value === filters.region);
    }
    if (filters.discipline) {
      return page.tag?.some(tag => tag.name === 'discipline' && tag.value === filters.discipline);
    }
    if (filters.region && filters.discipline) {
      return page.tag?.some(
        tag =>
          (tag.name === 'region' && tag.value === filters.region) ||
          (tag.name === 'discipline' && tag.value === filters.discipline)
      );
    }

    return true; // No filter, return all
  });

  // Filter for regionDiscipline based on currentPageType
  const regionDisciplineData = allPages.filter((page, index) => {
    // Handle both direct pageType and nested in attributes (API transformation inconsistency)
    const pageType = page.pageType || page.attributes?.pageType;
    const pageTags = page.tag || page.attributes?.tag || [];

    const isRegionDiscipline = pageType === 'region-discipline';

    if (!isRegionDiscipline) {
      return false;
    }

    // Apply filtering based on current page type
    switch (currentPageType) {
      case 'stories':
      case 'activities':
      case 'destinations':
      case 'article-category':
        return true; // No filter - show all region-discipline pages

      case 'discipline':
        const disciplineTag = currentTags.find(t => t.name === 'discipline');
        const disciplineMatch = disciplineTag && pageTags.some(tag => 
          tag.name === 'discipline' && tag.value === disciplineTag.value
        );
        return disciplineMatch;

      case 'country':
        const countryTag = currentTags.find(t => t.name === 'country');
        const countryMatch = countryTag && pageTags.some(tag => 
          tag.name === 'country' && tag.value === countryTag.value
        );
        return countryMatch;

      case 'region':
        const regionTag = currentTags.find(t => t.name === 'region');
        const regionMatch = regionTag && pageTags.some(tag => 
          tag.name === 'region' && tag.value === regionTag.value
        );
        return regionMatch;

      case 'region-discipline':
        const regionTagRD = currentTags.find(t => t.name === 'region');
        const regionMatchRD = regionTagRD && pageTags.some(tag => 
          tag.name === 'region' && tag.value === regionTagRD.value
        );
        return regionMatchRD;

      default:
        return true; // Default - show all
    }
  });

  const articleData = allPages.filter((page, index) => {
    // Handle both direct pageType and nested in attributes (API transformation inconsistency)
    const pageType = page.pageType || page.attributes?.pageType;
    const pageTags = page.tag || page.attributes?.tag || [];

    const isArticle = pageType === 'article';

    if (!isArticle) {
      return false;
    }

    // For individual article pages (pageType = article), match by category
    if (currentPageType === 'article') {
      // Find the category tag from the current article page
      const currentCategoryTag = currentTags.find(tag => tag.name === 'category');
      
      if (!currentCategoryTag) {
        return false; // If no category tag, don't show any articles
      }
      
      // Check if the article has a matching category tag
      const hasMatchingCategory = pageTags.some(pageTag => 
        pageTag.name === 'category' && pageTag.value === currentCategoryTag.value
      );
      
      // Article filter diagnostics removed to avoid verbose logging in production
      
      return hasMatchingCategory;
    }

    // For other page types (article-category, etc.), use the existing logic
    // OTHER TAGS FILTER (OR condition) - Exclude language tag from comparison
    const nonLanguageTags = currentTags.filter(tag => tag.name !== 'language');

    if (nonLanguageTags.length === 0) {
      return true; // If no non-language tags, return all articles in correct locale
    }

    const hasMatchingTag = nonLanguageTags.some(currentTag => {
      const tagMatch = pageTags.some(pageTag => {
        const match = pageTag.name === currentTag.name && pageTag.value === currentTag.value;
        return match;
      });
      return tagMatch;
    });

    return hasMatchingTag;
  });

  const categoryData = allPages.filter(page => {
    // Handle both direct pageType and nested in attributes (API transformation inconsistency)
    const pageType = page.pageType || page.attributes?.pageType;
    const pageTags = page.tag || page.attributes?.tag || [];

    // Apply filtering based on current page type
    switch (currentPageType) {
      case 'stories':
      case 'article-category':
      case 'article':
        return pageType === 'article-category';

      case 'activities':
        return pageType === 'discipline';

      case 'destinations':
        return pageType === 'country';

      case 'country':
        const countryTag = currentTags.find(t => t.name === 'country');
        if (pageType === 'region' && countryTag) {
          // Check if this region belongs to the current country
          return pageTags.some(tag => tag.name === 'country' && tag.value === countryTag.value);
        }
        return pageType === 'region';

      default:
        return pageType === 'discipline';
    }
  });

  return {
    listing: listingData,
    regionDiscipline: regionDisciplineData,
    article: articleData,
    category: categoryData,
  };
};
